import pluralize from "pluralize";

export const getResolvedDescription = (description, isVolume) =>
  String(description || "").trim() ||
  (isVolume ? "Select how many credits you want to add." : "");

export const getQuantityLabel = (name, quantity) => {
  const baseName = String(name || "")
    .replace(/^\d+\s+/, "")
    .toLowerCase();
  return pluralize(baseName, quantity);
};

export const getUniqueTiers = (tiers = []) => {
  if (!tiers.length) return [];

  const unique = [];
  const seenPrices = new Set();

  tiers.forEach((tier) => {
    if (seenPrices.has(tier.price)) return;

    seenPrices.add(tier.price);
    const samePriceTiers = tiers.filter((t) => t.price === tier.price);
    const startingUnit = samePriceTiers[0].startingUnit;
    const endingUnit = samePriceTiers[samePriceTiers.length - 1].endingUnit;

    unique.push({
      ...tier,
      startingUnit,
      endingUnit,
    });
  });

  return unique;
};

export const clampToStep = (
  rawValue,
  minQuantity,
  maxQuantity,
  quantityStep,
) => {
  const constrainedValue = Math.max(
    minQuantity,
    Math.min(maxQuantity, rawValue),
  );
  const steps = Math.round((constrainedValue - minQuantity) / quantityStep);
  return minQuantity + steps * quantityStep;
};

export const getMaxTierPrice = (uniqueTiers = []) => {
  if (!uniqueTiers.length) return 0;
  return Math.max(...uniqueTiers.map((tier) => tier.price));
};

export const getMaxBulkDiscount = (uniqueTiers = []) => {
  if (!uniqueTiers.length) return 0;
  const maxTierPrice = getMaxTierPrice(uniqueTiers);
  const minPrice = Math.min(...uniqueTiers.map((tier) => tier.price));
  if (!maxTierPrice || minPrice >= maxTierPrice) return 0;
  return Math.round(((maxTierPrice - minPrice) / maxTierPrice) * 100);
};

export const findCurrentTier = (uniqueTiers = [], quantity = 0) => {
  if (!uniqueTiers.length) return null;

  return (
    uniqueTiers.find((tier) => {
      if (!tier) return false;
      if (tier.endingUnit != null) {
        return quantity >= tier.startingUnit && quantity <= tier.endingUnit;
      }
      return quantity >= tier.startingUnit;
    }) || null
  );
};

export const getNextTier = (uniqueTiers = [], quantity = 0) => {
  if (!uniqueTiers.length) return null;
  const sortedTiers = [...uniqueTiers].sort(
    (a, b) => a.startingUnit - b.startingUnit,
  );
  return sortedTiers.find((tier) => tier.startingUnit > quantity) || null;
};

export const getSavingsPercent = (maxTierPrice, currentTierPrice) => {
  if (!maxTierPrice || currentTierPrice == null) return 0;
  if (currentTierPrice >= maxTierPrice) return 0;
  return Math.round(((maxTierPrice - currentTierPrice) / maxTierPrice) * 100);
};

export const getSliderPercent = (quantity, minQuantity, maxQuantity) => {
  const denom = maxQuantity - minQuantity;
  if (denom <= 0) return 0;
  return Math.max(0, Math.min(100, ((quantity - minQuantity) / denom) * 100));
};

export const getSliderStyle = (sliderPercent, usesAllowanceStyle) => {
  const fill = usesAllowanceStyle ? "#ee9b7a" : "#3b82f6";
  const track = "#d9dfe8";

  return {
    width: "100%",
    height: usesAllowanceStyle ? "8px" : "28px",
    background: `linear-gradient(to right, ${fill} 0%, ${fill} ${sliderPercent}%, ${track} ${sliderPercent}%, ${track} 100%)`,
    borderRadius: "999px",
    outline: "none",
    WebkitAppearance: "none",
    appearance: "none",
  };
};

export const getDefaultQuantity = (minQuantity, maxQuantity, quantityStep) => {
  const denom = maxQuantity - minQuantity;
  if (denom <= 0) return null;
  const target = minQuantity + denom * 0.3;
  return clampToStep(
    Math.round(target),
    minQuantity,
    maxQuantity,
    quantityStep,
  );
};

export const getPriceSubtext = (period, currency) =>
  `per ${period} ${currency === "GBP" ? "(excl VAT)" : ""}`;
